import type { GeoJsonGeometry, Pos } from '../lib/geo.ts'

/** Guidance to the area of one's task (M19): how far, and which way, in a straight line. Pure arithmetic, tested against known values; the screen
 *  says it is a straight line and that the course's paths are what one follows. Nothing here is stored or shared (sharing a position is
 *  `livePosition.ts`'s only). */

/** Mean Earth radius in metres (IUGG R1), the usual sphere of the haversine formula: good to about 0.5 % anywhere, far better at the scale of a course. */
export const EARTH_RADIUS_M = 6_371_008.8
const rad = (d: number) => (d * Math.PI) / 180
const deg = (r: number) => (r * 180) / Math.PI

/** Great-circle distance in metres between two positions ([lon, lat]), by the haversine formula. */
export function distanceM(a: Pos, b: Pos): number {
  const [lon1, lat1] = a, [lon2, lat2] = b
  const dLat = rad(lat2 - lat1), dLon = rad(lon2 - lon1)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Initial bearing (forward azimuth) from `from` to `to`, in degrees clockwise from true north, in [0, 360). */
export function bearingDeg(from: Pos, to: Pos): number {
  const [lon1, lat1] = from, [lon2, lat2] = to
  const y = Math.sin(rad(lon2 - lon1)) * Math.cos(rad(lat2))
  const x = Math.cos(rad(lat1)) * Math.sin(rad(lat2)) - Math.sin(rad(lat1)) * Math.cos(rad(lat2)) * Math.cos(rad(lon2 - lon1))
  return (deg(Math.atan2(y, x)) + 360) % 360
}

/** The eight words of a compass, clockwise from north (i18n keys `guide.dir.<code>`). */
export const COMPASS8 = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'] as const
export type Compass8 = (typeof COMPASS8)[number]
/** A bearing to one of eight 45° sectors, each centred on its direction (north is 337.5° to 22.5°). */
export function compass8(bearing: number): Compass8 {
  const b = ((bearing % 360) + 360) % 360
  return COMPASS8[Math.floor((b + 22.5) / 45) % 8]!
}

const M_PER_DEG = (EARTH_RADIUS_M * Math.PI) / 180
type Ring = Pos[]
/** Signed area and first moments of one ring, on a flat projection around `origin` (metres from it: small numbers, no loss of precision). */
function ringMoments(ring: Ring, origin: Pos): { a: number; cx: number; cy: number } {
  const kx = M_PER_DEG * Math.cos(rad(origin[1]))
  const pts = ring.map(([x, y]) => [(x - origin[0]) * kx, (y - origin[1]) * M_PER_DEG] as const)
  let a = 0, cx = 0, cy = 0
  for (let i = 0; i < pts.length; i++) {
    const [x0, y0] = pts[i]!, [x1, y1] = pts[(i + 1) % pts.length]!
    const f = x0 * y1 - x1 * y0
    a += f; cx += (x0 + x1) * f; cy += (y0 + y1) * f
  }
  return { a: a / 2, cx: cx / 6, cy: cy / 6 }          // cx, cy are the first moments (centre = moment / area)
}
const openRing = (r: Pos[]): Pos[] => (r.length > 1 && r[0]![0] === r[r.length - 1]![0] && r[0]![1] === r[r.length - 1]![1] ? r.slice(0, -1) : r)

/** The point to walk to for an area: its own point, or the centroid of its outline (holes taken out, the parts of a multi-polygon weighted by their
 *  size), or the middle of a line along its length. Null for a shape it cannot read. */
export function targetPoint(geom: GeoJsonGeometry | null | undefined): Pos | null {
  if (!geom) return null
  const c = geom.coordinates as unknown
  if (geom.type === 'Point') { const p = c as number[]; return Number.isFinite(p[0]) && Number.isFinite(p[1]) ? [p[0]!, p[1]!] : null }
  if (geom.type === 'MultiPoint') { const ps = c as Pos[]; return ps.length ? [ps.reduce((s, p) => s + p[0], 0) / ps.length, ps.reduce((s, p) => s + p[1], 0) / ps.length] : null }
  if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
    const lines = (geom.type === 'LineString' ? [c] : c) as Pos[][]
    let len = 0, mx = 0, my = 0
    for (const l of lines) for (let i = 1; i < l.length; i++) { const d = distanceM(l[i - 1]!, l[i]!); len += d; mx += ((l[i - 1]![0] + l[i]![0]) / 2) * d; my += ((l[i - 1]![1] + l[i]![1]) / 2) * d }
    return len > 0 ? [mx / len, my / len] : lines[0]?.[0] ? [lines[0][0][0], lines[0][0][1]] : null
  }
  if (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon') return null
  const polys = (geom.type === 'Polygon' ? [c] : c) as Pos[][][]
  const rings = polys.flatMap((p) => p.map(openRing)).filter((r) => r.length >= 3)
  if (!rings.length) return null
  const origin: Pos = [rings[0]![0]![0], rings[0]!.reduce((s, p) => s + p[1], 0) / rings[0]!.length]
  const kx = M_PER_DEG * Math.cos(rad(origin[1]))
  let A = 0, CX = 0, CY = 0
  for (const poly of polys) poly.forEach((r, i) => {
    const o = openRing(r)
    if (o.length < 3) return
    const m = ringMoments(o, origin)
    const sign = (i === 0 ? 1 : -1) * Math.sign(m.a || 1)          // the outer ring adds, holes take away, whatever their winding
    A += sign * m.a; CX += sign * m.cx; CY += sign * m.cy
  })
  if (Math.abs(A) < 1e-9) { const r = rings[0]!; return [r.reduce((s, p) => s + p[0], 0) / r.length, r.reduce((s, p) => s + p[1], 0) / r.length] }
  return [origin[0] + CX / A / kx, origin[1] + CY / A / M_PER_DEG]
}

/** A circle of `radiusM` metres around a position as a GeoJSON polygon (the accuracy of my position, drawn on the map). */
export function circlePolygon(center: Pos, radiusM: number, steps = 48): GeoJsonGeometry {
  const [lon, lat] = center
  const kx = M_PER_DEG * Math.cos(rad(lat))
  const ring: Pos[] = []
  for (let i = 0; i <= steps; i++) {
    const t = (2 * Math.PI * (i % steps)) / steps
    ring.push([lon + (radiusM * Math.sin(t)) / kx, lat + (radiusM * Math.cos(t)) / M_PER_DEG])
  }
  return { type: 'Polygon', coordinates: [ring] }
}

/** Everything the guidance screen shows for one position of mine and one target. */
export function guidance(me: Pos, target: Pos): { distanceM: number; bearing: number; direction: Compass8 } {
  const bearing = bearingDeg(me, target)
  return { distanceM: distanceM(me, target), bearing, direction: compass8(bearing) }
}

/** Where an area sits on the course, for "Voir sur la carte" (M21: location off, the area is shown without any position): its ancestors from the top
 *  down, the site's own root (the area with no parent) left out, e.g. a loop then a hole. Whatever the club's nesting is: no level is named here. */
export function areaPlace<T extends { id: string; parent_id: string | null }>(area: T | undefined, byId: ReadonlyMap<string, T>): T[] {
  const out: T[] = []
  const seen = new Set<string>()
  for (let p = area?.parent_id ? byId.get(area.parent_id) : undefined; p && p.parent_id && !seen.has(p.id); p = byId.get(p.parent_id)) {
    seen.add(p.id)
    out.unshift(p)
  }
  return out
}
